import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, ChevronDown, Crown, ExternalLink, Loader2, Lock, ShieldCheck, Ticket } from "lucide-react";
import { getMobilityResources } from "@/lib/mobility/mobility.functions";
import { buildMobilityTeaser, mobilityCopy, type MobilityGroup } from "@/lib/mobility/teaser";

type Props = {
  city: string;
  transport: string;
  lang?: string;
  /** Free/Premium: teaser com cadeado + CTA para o Routify Pro. */
  locked?: boolean;
  /** Título principal do cartão (ex.: "Bilhete recomendado: passe diário"). */
  title?: string;
  subtitle?: string;
};

/**
 * Cartão compacto único de mobilidade oficial.
 *
 * Substitui a antiga duplicação (cartão "Bilhete recomendado" + bloco grande
 * "Mobilidade oficial em …"). Avisos essenciais são visíveis em todos os
 * planos; os links oficiais só no Pro.
 */
export function MobilityTicketCard({ city, transport, lang = "pt", locked = false, title, subtitle }: Props) {
  const fetchResources = useServerFn(getMobilityResources);
  const [groups, setGroups] = useState<MobilityGroup[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const copy = mobilityCopy(lang);

  useEffect(() => {
    if (!city) return;
    let alive = true;
    setLoading(true);
    fetchResources({ data: { city, transport } })
      .then((r) => alive && setGroups(r.groups as MobilityGroup[]))
      .catch(() => alive && setGroups([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [city, transport, fetchResources]);

  const teaser = useMemo(
    () => buildMobilityTeaser({ groups: groups ?? [], locked, lang }),
    [groups, locked, lang],
  );

  if (!city) return null;
  if (!loading && teaser.empty && teaser.warnings.length === 0) return null;

  const heading = title ?? `${copy.title} — ${city}`;

  return (
    <div className="mt-3 rounded-2xl border border-brand/30 bg-brand/5 px-3 py-2.5 text-xs">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-brand-foreground">
          <Ticket className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-foreground">{heading}</div>
          {subtitle && <div className="text-muted-foreground">{subtitle}</div>}
        </div>
        {locked && (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand/15 px-1.5 py-0.5 text-[10px] font-bold text-brand">
            <Crown className="h-2.5 w-2.5" /> PRO
          </span>
        )}
      </div>

      {loading && (
        <div className="mt-2 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> …
        </div>
      )}

      {/* Avisos essenciais: sempre visíveis, em todos os planos. */}
      {teaser.warnings.length > 0 && (
        <ul className="mt-2 space-y-1">
          {teaser.warnings.map((w) => (
            <li key={w} className="flex items-start gap-1.5 rounded-lg bg-amber-500/10 p-1.5 text-[11px] text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Uma única acção compacta. */}
      {teaser.action.kind === "upgrade" && (
        <Link
          to="/pro"
          className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-brand/40 bg-background px-3 py-1.5 text-[12px] font-semibold text-brand transition hover:bg-brand/10"
        >
          <Lock className="h-3 w-3" />
          {teaser.action.label}
        </Link>
      )}

      {teaser.action.kind === "official" && (
        <a
          href={teaser.action.url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-2 inline-flex items-center gap-1.5 rounded-xl border border-brand/40 bg-background px-3 py-1.5 text-[12px] font-semibold text-brand transition hover:bg-brand/10"
        >
          {teaser.action.label}
          <span className="text-muted-foreground">· {teaser.action.providerName}</span>
          <ExternalLink className="h-3 w-3" />
        </a>
      )}

      {teaser.action.kind === "none" && !loading && teaser.warnings.length === 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground">{teaser.action.label}</p>
      )}

      {/* Painel compacto com os restantes recursos (apenas Pro). */}
      {teaser.panel.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
          >
            {copy.more} ({teaser.resourceCount - 1})
            <ChevronDown className={`h-3 w-3 transition ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="mt-1.5 space-y-2">
              {teaser.panel.map((g) => (
                <div key={g.mode}>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{g.label}</div>
                  <ul className="mt-0.5 space-y-0.5">
                    {g.items.map((r) => (
                      <li key={r.id}>
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:underline"
                        >
                          {r.providerName}
                          {r.official && <ShieldCheck className="h-3 w-3 text-emerald-600" />}
                          <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                        {r.notes && <div className="text-[10px] text-muted-foreground">{r.notes}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
